import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import LoginScreen from './screens/LoginScreen';
import CursoInfoScreen from './screens/CursoInfoScreen';
import DesenhoInfoScreen from './screens/DesenhoInfoScreen';
import HomeScreen from './screens/HomeScreen';
import AulaScreen from './screens/AulaScreen';
import './App.css';

const DISPOSITIVO_UNICO_ATIVO = false;

function App() {
  const [user, setUser] = useState(null);
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  const [deviceBlockMessage, setDeviceBlockMessage] = useState('');
  const [currentScreen, setCurrentScreen] = useState(() => {
    const saved = localStorage.getItem('ink_current_screen');
    return saved || 'home';
  });
  const [selectedLessonSlug, setSelectedLessonSlug] = useState(() => {
    return localStorage.getItem('ink_selected_lesson_slug') || '';
  });

  const [publicScreen, setPublicScreen] = useState(() => {
    const saved = localStorage.getItem('ink_public_screen');
    return saved === 'projetos' ? 'login' : saved || 'login';
  });

  const getLocalDeviceId = () => {
    const STORAGE_KEY = 'inkrodriguez_device_id';
    const existingId = localStorage.getItem(STORAGE_KEY);

    if (existingId) return existingId;

    const newId = `${Date.now()}_${Math.random().toString(36).slice(2)}_${navigator.userAgent.length}`;
    localStorage.setItem(STORAGE_KEY, newId);
    return newId;
  };

  const ensureAuthorizedDevice = async (currentUser) => {
    if (!DISPOSITIVO_UNICO_ATIVO) {
      return { ok: true };
    }

    const currentDeviceId = getLocalDeviceId();
    const deviceRef = doc(db, 'usuarios_dispositivos_autorizados', currentUser.uid);
    const deviceSnap = await getDoc(deviceRef);

    const nomeFinal = currentUser.displayName || 'Aluno';
    const emailFinal = currentUser.email || '';

    if (!deviceSnap.exists()) {
      await setDoc(deviceRef, {
        uid: currentUser.uid,
        nome: nomeFinal,
        email: emailFinal,
        authorizedDeviceId: currentDeviceId,
        firstUserAgent: navigator.userAgent,
        lastUserAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });

      return { ok: true };
    }

    const deviceData = deviceSnap.data();

    if (deviceData.authorizedDeviceId !== currentDeviceId) {
      return {
        ok: false,
        message:
          'Este dispositivo não está autorizado para esta conta. Use o aparelho original ou peça liberação manual.',
      };
    }

    await updateDoc(deviceRef, {
      nome: nomeFinal,
      email: emailFinal,
      lastUserAgent: navigator.userAgent,
      lastLoginAt: serverTimestamp(),
    });

    return { ok: true };
  };

  const openLessonScreen = (lessonSlug) => {
    setSelectedLessonSlug(lessonSlug);
    setCurrentScreen('lesson');
    localStorage.setItem('ink_selected_lesson_slug', lessonSlug);
    localStorage.setItem('ink_current_screen', 'lesson');
  };

  const goToHomeScreen = () => {
    setCurrentScreen('home');
    localStorage.setItem('ink_current_screen', 'home');
  };

  const goToPublicScreen = (screen) => {
    setPublicScreen(screen);
    localStorage.setItem('ink_public_screen', screen);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser) {
          setUser(null);
          setDeviceBlocked(false);
          setDeviceBlockMessage('');
          setCurrentScreen('home');
          localStorage.setItem('ink_current_screen', 'home');
          return;
        }

        const deviceValidation = await ensureAuthorizedDevice(currentUser);

        if (!deviceValidation.ok) {
          setDeviceBlocked(true);
          setDeviceBlockMessage(deviceValidation.message);
          await signOut(auth);
          setUser(null);
          return;
        }

        setDeviceBlocked(false);
        setDeviceBlockMessage('');
        setUser(currentUser);
      } catch (error) {
        console.error('Erro ao validar autenticação/dispositivo:', error);
        setUser(null);
        setDeviceBlocked(false);
        setDeviceBlockMessage('');
      }
    });

    return () => unsubscribe();
  }, []);

  if (deviceBlocked) {
    return (
      <div className="container2">
        <div className="container">
          <div className="auth-box">
            <h2>Dispositivo não autorizado</h2>
            <p>{deviceBlockMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    if (publicScreen === 'curso') {
      return <CursoInfoScreen onNavigate={goToPublicScreen} />;
    }

    if (publicScreen === 'desenho') {
      return <DesenhoInfoScreen onNavigate={goToPublicScreen} />;
    }

    return <LoginScreen onNavigate={goToPublicScreen} />;
  }

  if (currentScreen === 'lesson' && selectedLessonSlug) {
    return (
      <AulaScreen
        user={user}
        aulaSlug={selectedLessonSlug}
        onBack={goToHomeScreen}
      />
    );
  }

  return (
    <HomeScreen
      user={user}
      onOpenLesson={openLessonScreen}
    />
  );
}

export default App;
